/**
 * BoxScoreView Component
 *
 * Pure content component that renders live game box score data
 * including scoreboard, player stats tables, and tracked prop progress.
 * Contains no modal chrome — designed to be embedded in any container.
 */

import type { GameIdSource, GameTrackedLeg } from '@parlay-picker/shared/hooks';
import { useBoxScore, useOnCourtMap } from '@parlay-picker/shared/hooks';
import type { LeagueKey } from '@parlay-picker/shared/league';
import { fuzzyMatchPlayer } from '@parlay-picker/shared/playerNameUtils';
import { type ReactElement, useCallback, useMemo, useState } from 'react';
import { GameScoreboard } from './box-score/GameScoreboard';
import { PreGameView } from './box-score/PreGameView';
import { isBenchOrDNP, TeamBoxScore } from './box-score/TeamBoxScore';
import { TrackedPlayerCard } from './box-score/TrackedPlayerCard';
import { useBoxScoreData } from './box-score/useBoxScoreData';
import { useGameTrackedLegs } from './box-score/useGameTrackedLegs';
import { TeamLogo } from './TeamLogo';

// =============================================================================
// Types
// =============================================================================

export interface BoxScoreViewProps {
  gameId: string;
  gameIdSource: GameIdSource;
  leagueKey: LeagueKey;
  highlightedPlayers: string[];
  homeTeam?: string;
  awayTeam?: string;
  gameStartsAt?: string;
}

// =============================================================================
// Main Component
// =============================================================================

export function BoxScoreView({
  gameId,
  gameIdSource,
  leagueKey,
  highlightedPlayers,
  homeTeam: contextHomeTeam,
  awayTeam: contextAwayTeam,
  gameStartsAt,
}: BoxScoreViewProps): ReactElement {
  const [activeTab, setActiveTab] = useState<'away' | 'home'>('away');

  // Fetch tracked legs for the current game (works regardless of how view was opened)
  // Pass team names and tip for fallback matching (game picker uses BDL IDs, legs use Odds API IDs)
  const gameTrackedLegs = useGameTrackedLegs({
    gameId,
    leagueKey,
    homeTeam: contextHomeTeam,
    awayTeam: contextAwayTeam,
    gameStartsAt,
  });

  // Merge fetched tracked leg player names with any highlightedPlayers from the caller
  const mergedHighlightedPlayers = useMemo(() => {
    const names = new Set(highlightedPlayers.map((n) => n.toLowerCase()));
    for (const leg of gameTrackedLegs) {
      names.add(leg.player.toLowerCase());
    }
    return Array.from(names);
  }, [highlightedPlayers, gameTrackedLegs]);

  // Build a map: playerName (lowercase) -> GameTrackedLeg[] for prop display
  const playerLegsMap = useMemo(() => {
    const map = new Map<string, GameTrackedLeg[]>();
    for (const leg of gameTrackedLegs) {
      const key = leg.player.toLowerCase();
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(leg);
    }
    return map;
  }, [gameTrackedLegs]);

  // Fetch box score via REST (polls every 30s)
  const boxScoreData = useBoxScoreData(gameId || '__none__', gameIdSource, leagueKey);

  // Build box score view model (uses merged highlighted players)
  const viewModel = useBoxScore(boxScoreData, mergedHighlightedPlayers, leagueKey);

  // Detect which players are on the floor (compares minutes between polls)
  const onCourtMap = useOnCourtMap(boxScoreData);

  // Collect tracked players from both teams
  const trackedPlayers = useMemo(() => {
    if (!viewModel) return [];
    return [
      ...viewModel.awayTeam.players.filter((p) => p.isTracked),
      ...viewModel.homeTeam.players.filter((p) => p.isTracked),
    ];
  }, [viewModel]);

  /** Find tracked legs for a player using fuzzy matching */
  const getLegsForPlayer = useCallback(
    (playerName: string): GameTrackedLeg[] => {
      for (const [name, legs] of playerLegsMap) {
        if (fuzzyMatchPlayer(name, playerName)) return legs;
      }
      return [];
    },
    [playerLegsMap],
  );

  // Live/final: render scoreboard + box score
  if (viewModel) {
    return (
      <>
        {/* Game scoreboard */}
        <GameScoreboard
          homeTeam={viewModel.homeTeam}
          awayTeam={viewModel.awayTeam}
          quarterScores={viewModel.quarterScores}
          quarter={viewModel.quarter}
          timeRemaining={viewModel.timeRemaining}
          gameState={viewModel.gameState}
          leagueKey={leagueKey}
        />

        {/* Tracked legs — quick glance at your props */}
        {(trackedPlayers.length > 0 || gameTrackedLegs.length > 0) && (
          <div className="mt-3">
            <div className="text-[10px] font-medium text-theme-text-tertiary uppercase tracking-wider px-1 mb-1.5">
              Your Tracked Legs
            </div>
            <div className="max-h-[20vh] overflow-y-auto space-y-1.5">
              {/* Sort: active first by minutes DESC, DNP last */}
              {[...trackedPlayers]
                .sort((a, b) => {
                  const aDnp = isBenchOrDNP(a.min) ? 1 : 0;
                  const bDnp = isBenchOrDNP(b.min) ? 1 : 0;
                  if (aDnp !== bDnp) return aDnp - bDnp;
                  // Parse minutes for DESC sort (e.g., "32:15" -> 32.25)
                  const aMin = parseFloat(a.min?.split(':')[0] || '0');
                  const bMin = parseFloat(b.min?.split(':')[0] || '0');
                  return bMin - aMin;
                })
                .map((p) => (
                  <TrackedPlayerCard
                    leagueKey={leagueKey}
                    key={p.playerId}
                    player={p}
                    trackedLegs={getLegsForPlayer(p.playerName)}
                    gameState={viewModel?.gameState}
                    isOnCourt={
                      viewModel?.gameState === 'live' && onCourtMap.get(p.playerId) === true
                    }
                  />
                ))}
              {/* Show legs for players not found in box score (pre-game or missing) */}
              {gameTrackedLegs
                .filter(
                  (leg) => !trackedPlayers.some((p) => fuzzyMatchPlayer(leg.player, p.playerName)),
                )
                .filter(
                  (leg, idx, arr) =>
                    arr.findIndex((l) => fuzzyMatchPlayer(l.player, leg.player)) === idx,
                )
                .map((leg) => (
                  <TrackedPlayerCard
                    leagueKey={leagueKey}
                    key={leg.player}
                    playerName={leg.player}
                    trackedLegs={getLegsForPlayer(leg.player)}
                    gameState={viewModel?.gameState}
                  />
                ))}
            </div>
          </div>
        )}

        {/* Team tabs + box score — only show when player data exists */}
        {(viewModel.awayTeam.players.length > 0 || viewModel.homeTeam.players.length > 0) && (
          <>
            <div className="mt-3 grid grid-cols-2 border-b border-theme-border/30">
              <button
                type="button"
                onClick={() => setActiveTab('away')}
                className={`flex items-center justify-center gap-1.5 py-2 text-xs font-medium transition-colors border-b-2 ${
                  activeTab === 'away'
                    ? 'border-[var(--color-primary)] text-white'
                    : 'border-transparent text-theme-text-tertiary hover:text-theme-text-secondary'
                }`}
              >
                <TeamLogo name={viewModel.awayTeam.name} leagueKey={leagueKey} size="sm" />
                {viewModel.awayTeam.abbr}
              </button>
              <button
                type="button"
                onClick={() => setActiveTab('home')}
                className={`flex items-center justify-center gap-1.5 py-2 text-xs font-medium transition-colors border-b-2 ${
                  activeTab === 'home'
                    ? 'border-[var(--color-primary)] text-white'
                    : 'border-transparent text-theme-text-tertiary hover:text-theme-text-secondary'
                }`}
              >
                <TeamLogo name={viewModel.homeTeam.name} leagueKey={leagueKey} size="sm" />
                {viewModel.homeTeam.abbr}
              </button>
            </div>

            {/* Box score table — one team at a time */}
            <div className="mt-2">
              <TeamBoxScore
                team={viewModel.awayTeam}
                isActive={activeTab === 'away'}
                gameState={viewModel.gameState}
                onCourtMap={onCourtMap}
              />
              <TeamBoxScore
                team={viewModel.homeTeam}
                isActive={activeTab === 'home'}
                gameState={viewModel.gameState}
                onCourtMap={onCourtMap}
              />
            </div>
          </>
        )}
      </>
    );
  }

  // Pre-game: show matchup view if team names are available
  if (contextHomeTeam && contextAwayTeam) {
    return (
      <>
        <PreGameView
          homeTeam={contextHomeTeam}
          awayTeam={contextAwayTeam}
          gameStartsAt={gameStartsAt}
          leagueKey={leagueKey}
        />
        {/* Tracked legs — show props even before game starts */}
        {mergedHighlightedPlayers.length > 0 && (
          <div className="mt-3">
            <div className="text-[10px] font-medium text-theme-text-tertiary uppercase tracking-wider px-1 mb-1.5">
              Your Tracked Legs
            </div>
            <div className="space-y-1.5">
              {mergedHighlightedPlayers
                .filter((name, idx, arr) => arr.findIndex((n) => fuzzyMatchPlayer(n, name)) === idx)
                .map((name) => (
                  <TrackedPlayerCard
                    leagueKey={leagueKey}
                    key={name}
                    playerName={name}
                    trackedLegs={getLegsForPlayer(name)}
                    gameState="pre-game"
                  />
                ))}
            </div>
          </div>
        )}
      </>
    );
  }

  // Loading skeleton
  return (
    <div className="py-6 space-y-4 animate-pulse">
      {/* Skeleton scoreboard */}
      <div className="flex items-center justify-between px-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-theme-surface-hover" />
          <div className="w-12 h-4 rounded bg-theme-surface-hover" />
        </div>
        <div className="w-16 h-6 rounded bg-theme-surface-hover" />
        <div className="flex items-center gap-3">
          <div className="w-12 h-4 rounded bg-theme-surface-hover" />
          <div className="w-10 h-10 rounded-full bg-theme-surface-hover" />
        </div>
      </div>
      {/* Skeleton table rows */}
      <div className="space-y-2 px-2">
        {[1, 2, 3, 4, 5].map((i) => (
          <div key={i} className="flex items-center gap-3">
            <div className="w-24 h-3 rounded bg-theme-surface-hover" />
            <div className="flex-1 h-3 rounded bg-theme-surface-hover" />
          </div>
        ))}
      </div>
    </div>
  );
}
